import { useState } from 'react'
import { Alert, Box, Card, CardContent, Chip, Stack, Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { useSnackbar } from 'notistack'
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined'
import { wfCategoryReport, serverMessage } from './api'

/**
 * Item 6 — one merchant's categories as THEY typed them in the product master, the blanks they
 * still have to fill (through the SKU upload sheet), and the near-duplicate spellings Prozo may
 * want to raise with them. Read-only: nothing here merges or rewrites a merchant's text.
 */
export default function CategoryReportCard({ merchant }) {
  const { enqueueSnackbar } = useSnackbar()
  const [report, setReport] = useState(null)
  const [loading, setLoading] = useState(false)

  const load = async () => {
    if (!merchant) return
    setLoading(true)
    try {
      setReport(await wfCategoryReport(merchant.value))
    } catch (err) {
      enqueueSnackbar(serverMessage(err, 'Could not load the category report'), { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Card sx={{ maxWidth: 720, mt: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', borderRadius: '8px' }}>
      <CardContent>
        <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1 }}>
          <CategoryOutlinedIcon sx={{ fontSize: 18, color: '#5e35b1' }} />
          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Categories in the merchant's product master</Typography>
        </Stack>
        <Typography sx={{ fontSize: 12, color: '#4b5563', mb: 1.5 }}>
          A category-level weight freeze must name one of these, exactly as the merchant typed it. Blanks are filled by the
          merchant through their SKU upload sheet; until at least one category exists, category-level freezes are unavailable to them.
        </Typography>
        <LoadingButton variant='outlined' size='small' loading={loading} disabled={!merchant} onClick={load} sx={{ fontSize: 12 }}>
          {report ? 'Reload' : 'Load the category report'}
        </LoadingButton>

        {report && (
          <Box sx={{ mt: 2, display: 'grid', gap: 1.5 }}>
            <Alert severity={report.categoryFreezeAvailable ? 'success' : 'warning'} sx={{ fontSize: 12 }}>
              {report.products} products · {report.categorised} with a category · {report.blanks.count} blank.{' '}
              {report.categoryFreezeAvailable
                ? 'Category-level freezes are available to this merchant.'
                : 'Category-level freezes are NOT available until the merchant fills at least one category.'}
              {' '}(field: {report.field})
            </Alert>

            <Box>
              <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                Categories, most products first
              </Typography>
              <Stack direction='row' spacing={0.5} flexWrap='wrap' rowGap={0.5}>
                {report.categories.length === 0 && <Typography sx={{ fontSize: 12, color: '#6b7280' }}>None yet.</Typography>}
                {report.categories.map((c) => <Chip key={c.value} size='small' label={`${c.value} · ${c.count}`} sx={{ height: 22, fontSize: 11, bgcolor: '#f3f4f6' }} />)}
              </Stack>
            </Box>

            <Box>
              <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                Probable duplicates — raise with the merchant, nothing is merged here
              </Typography>
              {report.nearDuplicates.length === 0
                ? <Typography sx={{ fontSize: 12, color: '#6b7280' }}>No near-duplicate spellings found.</Typography>
                : report.nearDuplicates.map((group) => (
                  <Typography key={group.join('|')} sx={{ fontSize: 12, color: '#374151' }}>{group.join(' / ')}</Typography>
                ))}
            </Box>

            {report.blanks.count > 0 && (
              <Box>
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                  Blank category — first {report.blanks.sampleSkus.length} of {report.blanks.count} SKUs
                </Typography>
                <Stack direction='row' spacing={0.5} flexWrap='wrap' rowGap={0.5}>
                  {report.blanks.sampleSkus.map((s) => <Chip key={s} size='small' label={s} sx={{ height: 20, fontSize: 10.5, bgcolor: '#fff7ed', color: '#c2410c' }} />)}
                </Stack>
              </Box>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  )
}
